import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import './index.css'
import App from './App.tsx'
import config from '../amplify_outputs.json'; // This file is generated by the sandbox
import { Amplify } from 'aws-amplify';
import { QueryProvider } from './app/providers/QueryProvider'
import { AuthProvider } from './features/auth/contexts/AuthProvider';
import i18n from './i18n'; // Import i18n configuration
import * as Sentry from "@sentry/react";
import { ErrorFallback } from './components/sentry/ErrorFallback';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { ServiceWorkerManager } from './utils/service-worker';
import { ErrorDialogProvider } from './components/providers/ErrorDialogProvider';
import { isChunkLoadError } from './utils/chunk-load-error';

/**
 * ConditionalAuthProvider
 * 
 * Conditionally applies AWS Cognito authentication based on the current route.
 * This optimization prevents unnecessary Cognito API calls on public routes,
 * improving performance and reducing costs.
 * 
 * Public routes (no Cognito auth needed):
 * - /wellness-center/* - Uses invite code authentication
 * - /survey/* - Uses access code authentication
 * - /wellness/login, /wellness/signup, /wellness/verify - Uses magic-link authentication
 * 
 * All other routes require Cognito authentication and will be wrapped with AuthProvider.
 */
function ConditionalAuthProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  // Define public routes that don't need Cognito authentication
  const isPublicRoute = location.pathname.startsWith('/wellness-center') || 
                        location.pathname.startsWith('/survey') ||
                        location.pathname.startsWith('/wellness');
  
  // For public routes, skip AuthProvider to avoid unnecessary Cognito calls
  if (isPublicRoute) {
    return <>{children}</>;
  }
  
  // For authenticated routes, wrap with AuthProvider
  return <AuthProvider>{children}</AuthProvider>;
}

// Initialize Sentry only during production builds (not bare vite serve unless enabled)
const isBuildFlag = import.meta.env.VITE_IS_BUILD;
const isProductionBuild = isBuildFlag === 'true' || isBuildFlag === true;
if (isProductionBuild || import.meta.env.VITE_ENABLE_SENTRY_DEV === 'true') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_NODE_ENV || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture network details including request/response bodies
        networkDetailAllowUrls: [/.*/], // Capture all URLs
        networkCaptureBodies: true, // Capture both request and response bodies
      }),
      // Sentry.httpClientIntegration({
      //   // Disable automatic error capture since we handle it manually in apiClient
      //   failedRequestStatusCodes: [], // Empty array = no automatic capture
      // }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        showBranding: false,
        // Screenshot helps diagnose blank-question / layout issues on school devices
        enableScreenshot: true,
        // Hide auto-injected widget on mobile; participant survey uses SurveyHelpButton instead
        autoInject: (() => {
          // Check if device is mobile based on user agent and screen size
          const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                           window.innerWidth <= 768;
          return !isMobile;
        })(),
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Expected after deploys when a tab still references old hashed assets
    ignoreErrors: [
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
      /Importing a module script failed/i,
      /Loading chunk [\w-]+ failed/i,
      /Unable to preload CSS/i,
      /ChunkLoadError/i,
    ],
    // Error Capturing
    beforeSend(event, hint) {
      if (isChunkLoadError(hint?.originalException)) {
        return null;
      }
      return event;
    },
    // Enable maximum context capture
    maxBreadcrumbs: 100,
    attachStacktrace: true,
    sendDefaultPii: false,
    // Transaction Filtering - reduce noise from resource loading spans
    beforeSendTransaction(event) {
      // Filter out resource.script spans to reduce noise
      if (event.spans) {
        event.spans = event.spans.filter(span => {
          // Keep non-resource spans and important resource types
          return !(span.op === 'resource.script' || 
                  span.op === 'resource.css' || 
                  span.op === 'resource.img' ||
                  span.op === 'resource.font');
        });
      }
      
      // If this is a resource loading transaction, filter it out entirely
      if (event.transaction && 
          (event.transaction.includes('resource.script') || 
           event.transaction.includes('resource.css') ||
           event.transaction.includes('resource.img') ||
           event.transaction.includes('resource.font'))) {
        return null;
      }
      
      return event;
    },
    // Add release information
    release: import.meta.env.VITE_APP_VERSION || 'unknown',
    // Add user context
    initialScope: {
      tags: {
        component: 'frontend',
        framework: 'react',
      },
    },
  });
}

// Set document title based on environment
const baseTitle = 'Terrace Metrics';
const mode = import.meta.env.MODE;
let title = baseTitle;

// Check MODE first as it's set by Vite's --mode flag
if (mode === 'staging') {
  title = `${baseTitle} [staging]`;
} else if (mode === 'development') {
  title = `${baseTitle} [dev]`;
}

document.title = title;

// Only override if environment variables are set
if (import.meta.env.VITE_REACT_APP_USER_POOL_ID) {
  config.auth.user_pool_id = import.meta.env.VITE_REACT_APP_USER_POOL_ID;
}
if (import.meta.env.VITE_REACT_APP_USER_POOL_WEB_CLIENT_ID) {
  config.auth.user_pool_client_id = import.meta.env.VITE_REACT_APP_USER_POOL_WEB_CLIENT_ID;
}

Amplify.configure(config);

// Register service worker only in production BUILDS (not dev server)
if (import.meta.env.VITE_IS_BUILD && import.meta.env.VITE_NODE_ENV === 'production') {
  ServiceWorkerManager.register().catch((error) => {
    console.warn('Service Worker registration failed:', error);
  });
}
// Wait for i18n to be ready before rendering the app
let appRendered = false;

function renderApp() {
  if (appRendered) return;
  appRendered = true;
  
  const ErrorBoundaryWrapper = (isProductionBuild || import.meta.env.VITE_ENABLE_SENTRY_DEV === 'true')
    ? Sentry.ErrorBoundary 
    : ({ children }: { children: React.ReactNode }) => <>{children}</>;

  // Strip one-shot cache-bust param added by chunk reload recovery
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_reload')) {
      url.searchParams.delete('_reload');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    // ignore
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {/* showDialog disabled: chunk-load recovery + ErrorFallback handle UX; Sentry crash dialog felt alarming */}
      <ErrorBoundaryWrapper fallback={ErrorFallback} showDialog={false}>
        <Router>
          <ErrorDialogProvider>
            <ConditionalAuthProvider>
              <QueryProvider>
                <FeedbackProvider>
                  <App />
                </FeedbackProvider>
              </QueryProvider>
            </ConditionalAuthProvider>
          </ErrorDialogProvider>
        </Router>
      </ErrorBoundaryWrapper>
    </StrictMode>,
  )
}

if (i18n.isInitialized) {
  renderApp();
} else {
  i18n.on('initialized', renderApp);
}

